import './HomePage.css';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from './context/ThemeContext';
import PageHeader from './components/PageHeader.js';
import SiteFooter from './components/SiteFooter.js';
import { getUnifiedToolsFor } from './unifiedTools.js';
import PlusComposerMenu from './components/PlusComposerMenu.js';

const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'zh', name: 'Chinese (\u7B80\u4F53)' },
  { code: 'ja', name: 'Japanese' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'ru', name: 'Russian' },
  { code: 'ko', name: 'Korean' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' },
  { code: 'nl', name: 'Dutch' },
  { code: 'tr', name: 'Turkish' },
  { code: 'pl', name: 'Polish' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'th', name: 'Thai' },
  { code: 'id', name: 'Indonesian' },
  { code: 'sv', name: 'Swedish' },
  { code: 'ro', name: 'Romanian' },
  { code: 'el', name: 'Greek' },
  { code: 'cs', name: 'Czech' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'da', name: 'Danish' },
  { code: 'fi', name: 'Finnish' },
  { code: 'no', name: 'Norwegian' },
  { code: 'he', name: 'Hebrew' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ms', name: 'Malay' },
  { code: 'bn', name: 'Bengali' },
];

const HOME_ACTIONS = [
  {
    label: 'Build website',
    text: 'Build me a coffee shop website for people nearby. They should see the menu and find the shop.',
  },
  {
    label: 'Create slides',
    text: 'Make a short slide deck about launching a new product.',
  },
  {
    label: 'Video',
    text: 'Edit the video',
  },
];

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceHint, setVoiceHint] = useState('');
  const voiceHintTimerRef = useRef(null);
  const [browserOperatorEnabled, setBrowserOperatorEnabled] = useState(() => {
    try {
      return localStorage.getItem('zolaid-browser-operator-enabled') === 'true';
    } catch (_) {
      return false;
    }
  });
  const [autonomousAgentEnabled, setAutonomousAgentEnabled] = useState(() => {
    try {
      return localStorage.getItem('zolaid-autonomous-agent-enabled') === 'true';
    } catch (_) {
      return false;
    }
  });
  const plusButtonRef = useRef(null);
  const plusMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const openChat = (message, files = selectedFiles) => {
    const text = String(message || '').trim();
    if (!text && files.length === 0) return;
    navigate('/chatbot', { state: { initialMessage: text, files } });
  };

  const openVoiceCall = () => {
    navigate('/chatbot', { state: { voiceCall: true } });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    openChat(input);
  };

  const startVoiceInput = () => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      setVoiceHint('Speech recognition is not available in this browser.');
      if (voiceHintTimerRef.current) clearTimeout(voiceHintTimerRef.current);
      voiceHintTimerRef.current = setTimeout(() => setVoiceHint(''), 2500);
      return;
    }
    try {
      const recognition = new SpeechRecognitionAPI();
      recognition.lang = 'en-US';
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.onstart = () => setListening(true);
      recognition.onend = () => setListening(false);
      recognition.onerror = () => setListening(false);
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput((prev) => (prev ? `${prev} ${transcript}` : transcript));
      };
      recognition.start();
    } catch (_) {
      setListening(false);
      setVoiceHint('Speech recognition could not start.');
      if (voiceHintTimerRef.current) clearTimeout(voiceHintTimerRef.current);
      voiceHintTimerRef.current = setTimeout(() => setVoiceHint(''), 2500);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showPlusMenu &&
        plusButtonRef.current &&
        plusMenuRef.current &&
        !plusButtonRef.current.contains(event.target) &&
        !plusMenuRef.current.contains(event.target)
      ) {
        setShowPlusMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPlusMenu]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
      if (e.target) e.target.value = '';
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const persistFlag = (key, value) => {
    try {
      localStorage.setItem(key, value.toString());
    } catch (_) {
      /* ignore */
    }
  };

  const navigateToChatbot = (action = null) => {
    navigate('/chatbot', { state: { initialMessage: input.trim(), files: selectedFiles, action } });
  };

  const unifiedHomepagePlusTools = getUnifiedToolsFor('chatbotPlus');

  const handleToggleBrowser = () => {
    const next = !browserOperatorEnabled;
    setBrowserOperatorEnabled(next);
    persistFlag('zolaid-browser-operator-enabled', next);
    setShowPlusMenu(false);
    if (next) navigateToChatbot('browser-preview');
  };

  const handleToggleAutonomous = () => {
    const next = !autonomousAgentEnabled;
    setAutonomousAgentEnabled(next);
    persistFlag('zolaid-autonomous-agent-enabled', next);
    setShowPlusMenu(false);
    if (next) navigateToChatbot('autonomous-agent');
  };

  const handleUnifiedToolClick = (tool) => {
    if (!tool) return;
    if (tool.kind === 'route' && tool.route) {
      navigate(tool.route);
      return;
    }
    navigateToChatbot(tool.key);
  };

  return (
    <div className="homepage-root min-h-screen flex flex-col">
      <PageHeader variant="hero" />
      <main id="main-content" className="homepage-stage" tabIndex={-1}>
        <h1 className="homepage-prompt">What should I work on?</h1>
        <form className="homepage-composer" onSubmit={handleSubmit}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="homepage-file-input"
            onChange={handleFileSelect}
            onClick={(e) => {
              e.target.value = '';
            }}
            accept="image/*,video/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
          />
          <div className="homepage-typing-box">
            {selectedFiles.length > 0 && (
              <div className="homepage-selected-files">
                {selectedFiles.map((file, idx) => (
                  <div key={idx} className="homepage-file-item">
                    <span className="homepage-file-name">{file.name}</span>
                    <button
                      type="button"
                      className="homepage-remove-file"
                      onClick={() => removeFile(idx)}
                      aria-label={`Remove ${file.name}`}
                    >
                      {'\u00D7'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <textarea
              className="homepage-typing-input"
              placeholder="Assign a task or ask anything"
              value={input}
              rows={2}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  openChat(input);
                }
              }}
            />
            <div className="homepage-typing-box__bar">
              <div className="homepage-plus-wrapper">
                <button
                  ref={plusButtonRef}
                  type="button"
                  className="homepage-plus-btn"
                  aria-expanded={showPlusMenu}
                  aria-haspopup="menu"
                  aria-label="Add files and more"
                  onClick={() => setShowPlusMenu((open) => !open)}
                  title="Add files and more"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
                {showPlusMenu && (
                  <>
                    <div
                      className="homepage-menu-overlay"
                      onClick={() => setShowPlusMenu(false)}
                    />
                    <div ref={plusMenuRef}>
                      <PlusComposerMenu
                        tools={unifiedHomepagePlusTools}
                        onUploadFiles={() => {
                          if (fileInputRef.current) fileInputRef.current.click();
                          setShowPlusMenu(false);
                        }}
                        onToolClick={(tool) => {
                          handleUnifiedToolClick(tool);
                          setShowPlusMenu(false);
                        }}
                        browserEnabled={browserOperatorEnabled}
                        onToggleBrowser={handleToggleBrowser}
                        autonomousEnabled={autonomousAgentEnabled}
                        onToggleAutonomous={handleToggleAutonomous}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="homepage-typing-voice-pair" role="group" aria-label="Composer actions">
                <button
                  type="button"
                  className={`homepage-typing-voice${listening ? ' homepage-typing-voice--listening' : ''}`}
                  aria-label="Voice assistance"
                  aria-pressed={listening}
                  title={voiceHint || 'Voice assistance'}
                  onClick={startVoiceInput}
                >
                  {listening ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" />
                      <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" />
                      <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  )}
                </button>
                {input.trim() || selectedFiles.length > 0 ? (
                  <button
                    type="submit"
                    className="homepage-typing-submit"
                    aria-label="Send"
                    title="Send"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <line x1="12" y1="19" x2="12" y2="5" />
                      <polyline points="5 12 12 5 19 12" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="homepage-typing-voice homepage-typing-voice-call"
                    aria-label="Voice call"
                    title="Voice call"
                    onClick={openVoiceCall}
                  >
                    <span className="voice-wave" aria-hidden="true">
                      <span className="voice-wave-bar" />
                      <span className="voice-wave-bar" />
                      <span className="voice-wave-bar" />
                      <span className="voice-wave-bar" />
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
          {voiceHint ? (
            <p className="homepage-voice-hint" role="status" aria-live="polite">{voiceHint}</p>
          ) : null}
        </form>
        <div className="homepage-actions" role="group" aria-label="Ways to start">
          {HOME_ACTIONS.map((action) => (
            <button
              key={action.label}
              type="button"
              className="homepage-action"
              onClick={() => navigate('/chatbot', { state: { initialMessage: action.text } })}
            >
              {action.label}
            </button>
          ))}
        </div>
      </main>

      <div className="homepage-footer-wrap">
        <SiteFooter>
          <div className="footer-bottom-bar homepage-footer-bottom flex flex-col md:flex-row items-center justify-between text-xs w-full gap-4">
            <div className="mb-0 md:mb-0">{t('copyright')}</div>
            <div className="flex items-center gap-4 flex-wrap justify-center md:justify-end">
              <button
                type="button"
                onClick={() => setTheme('system')}
                className={`text-lg cursor-pointer hover:scale-110 transition-transform ${
                  theme === 'system' ? 'opacity-100' : 'opacity-50'
                }`}
                title="System Theme"
                aria-label="System Theme"
              >
                {'\u{1F5A5}'}
              </button>
              <button
                type="button"
                onClick={() => setTheme('light')}
                className={`text-lg cursor-pointer hover:scale-110 transition-transform ${
                  theme === 'light' ? 'opacity-100' : 'opacity-50'
                }`}
                title="Light Theme"
                aria-label="Light Theme"
              >
                {'\u{2609}'}
              </button>
              <button
                type="button"
                onClick={() => setTheme('dark')}
                className={`text-lg cursor-pointer hover:scale-110 transition-transform ${
                  theme === 'dark' ? 'opacity-100' : 'opacity-50'
                }`}
                title="Dark Theme"
                aria-label="Dark Theme"
              >
                {'\u{263E}'}
              </button>
              <div className="flex items-center gap-2">
                <span aria-hidden>{'\u{1F310}'}</span>
                <select
                  value={i18n.language}
                  onChange={(e) => {
                    const next = e.target.value;
                    i18n.changeLanguage(next);
                    try {
                      localStorage.setItem('zolaid-language', next);
                    } catch (_) {}
                  }}
                  className="language-select text-xs bg-transparent"
                  aria-label="Select language"
                >
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </SiteFooter>
      </div>
    </div>
  );
}
